import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../supabase";
import HomeNav from "./HomeNav";
import { EyeIcon, EyeOffIcon } from "./icons/EyeIcon";
import {
  validatePassword,
  PASSWORD_REQUIREMENTS_TEXT,
} from "../lib/validatePassword";

const CompanySignup = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    companyName: "",
    name: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    const { email, password, companyName, name } = formData;

    const passwordError = validatePassword(password);
    if (passwordError) {
      alert(passwordError);
      setLoading(false);
      return;
    }

    try {
      await supabase.auth.signOut();

      // Step 1: Attempt the Signup
      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email,
          password,
        });

      if (signUpError) throw signUpError;

      let currentSession = signUpData.session;
      let userId = signUpData.user?.id;

      // 🚨 THE FALLBACK DIAGNOSTIC 🚨
      // If Supabase silently swallowed an error and gave us a null session,
      // we force a manual login to expose the true error message.
      if (!currentSession) {
        console.log(
          "Signup returned null session. Forcing manual login check..."
        );

        const { data: loginData, error: loginError } =
          await supabase.auth.signInWithPassword({
            email,
            password,
          });

        if (loginError) {
          // This will throw the REAL error to your screen
          throw new Error(`Signup process failed: ${loginError.message}`);
        }

        currentSession = loginData.session;
        userId = loginData.user?.id;
      }

      // Final safety net
      if (!currentSession || !userId) {
        throw new Error(
          "Critical Auth Failure: Could not establish a session."
        );
      }

      // Step 2: Create a new company
      const { data: companyData, error: companyError } = await supabase
        .from("Organizations")
        .insert([{ name: companyName }])
        .select()
        .single();

      if (companyError) throw companyError;

      // Step 3: Add the user as an Admin in the `Users` table
      const { error: profileError } = await supabase.from("Users").insert([
        {
          id: userId,
          company_id: companyData.id,
          role: "Admin",
          name: name,
          email: email,
        },
      ]);

      if (profileError) throw profileError;

      // Route to dashboard
      setTimeout(() => {
        navigate(`/organization-portal/${companyData.id}`);
      }, 500);
    } catch (error) {
      console.error("Signup error:", error);
      alert(error.message); // This will now display the exact reason it's failing
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HomeNav />
      <div style={{ background: "#3a886f" }}>
        <div style={styles.pageContainer}>
          <div style={styles.card}>
            <img
              style={styles.logo}
              src="/Long_Black_Transparent.png"
              alt="Logo"
            />
            <h1 style={styles.title}>
              AI-powered insights tailored to boost your brand.
            </h1>
            <p style={styles.subtitle}>
              Welcome! Please complete the form to signup your company.
            </p>

            <form onSubmit={handleSignup} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Company Name</label>
                <input
                  style={styles.input}
                  type="text"
                  name="companyName"
                  placeholder="Company Name"
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Name</label>
                <input
                  style={styles.input}
                  type="text"
                  name="name"
                  placeholder="Name"
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Admin Email</label>
                <input
                  style={styles.input}
                  type="email"
                  name="email"
                  placeholder="Admin Email"
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.passwordWrapper}>
                  <input
                    style={{ ...styles.input, ...styles.passwordInput }}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={styles.eyeButton}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                  {PASSWORD_REQUIREMENTS_TEXT}
                </div>
              </div>
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(loading ? styles.buttonDisabled : {}),
                }}
                disabled={loading}
              >
                {loading ? "Signing Up..." : "Sign Up"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 20px",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: "14px",
    border: "1px solid #e5e7eb",
    padding: "40px",
    width: "65%",
    maxWidth: "900px",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  logo: {
    width: "180px",
    marginBottom: "24px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "800",
    color: "#111827",
    margin: "0 0 12px 0",
    textAlign: "center",
  },
  subtitle: {
    fontSize: "14px",
    color: "#676767",
    textAlign: "center",
    margin: "0 0 24px 0",
  },
  form: {
    width: "100%",
    maxWidth: "480px",
  },
  inputGroup: {
    marginBottom: "16px",
    width: "100%",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "700",
    color: "#4b5563",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "13px 16px",
    borderRadius: "10px",
    border: "1.5px solid #c4cad3",
    fontSize: "14px",
    color: "#111827",
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.2s",
  },
  passwordWrapper: {
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  passwordInput: {
    display: "block",
    paddingRight: "40px",
  },
  eyeButton: {
    position: "absolute",
    right: "10px",
    top: 0,
    bottom: 0,
    margin: "auto 0",
    height: "18px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  button: {
    width: "100%",
    background: "#00876e",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "12px 14px",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "14px",
    marginTop: "12px",
    transition: "background 0.2s",
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },
};

export default CompanySignup;
